import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Autocomplete, Box, FormControl, MenuItem, Select, TextField } from '@mui/material'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { isNil } from 'lodash'

import { useStateContext } from '../../context/ContextProvider'
import axios from '../../lib/axios'
import Loading from '../../components/Loading'
import TableShared from '../../components/TableShared'
import { isValid, handleAxiosError } from '../../helpers/helper'
import { TEXTFIELD_STYLE } from '../../constants/constants'

const ShowRouting = () => {
    const { t } = useTranslation()
    const { config, pusher } = useStateContext()
    const params = useParams()
    const navigate = useNavigate()
    const location = useLocation()

    const [isLoading, setIsLoading] = useState(false)
    const [sites, setSites] = useState([])
    const [statusOptions, setStatusOptions] = useState([])
    const [workCenters, setWorkCenters] = useState([])
    const [rateUomOptions, setRateUomOptions] = useState([])
    const [operationOptions, setOperationOptions] = useState([])

    /* form */
    const [code, setCode] = useState('')
    const [name, setName] = useState('')
    const [siteId, setSiteId] = useState('')
    const [siteName, setSiteName] = useState('')
    const [description, setDescription] = useState('')
    const [status, setStatus] = useState('')

    const [operations, setOperations] = useState([])

    const goBack = () => {
        navigate((!isNil(location.state) && !isNil(location.state.prevPathname)) ? location.state.prevPathname : '/routings')
    }

    useEffect(() => {
        getRouting()
    }, [params.id])

    /* real-time: refresh if another user updates this routing, leave if they delete it */
    useEffect(() => {
        const clientId = localStorage.getItem('client_id')
        const channelUpdated = pusher.subscribe(`${clientId}-routing-updated`)
        const channelDeleted = pusher.subscribe(`${clientId}-routing-deleted`)

        channelUpdated.bind(`${clientId}-routing-updated-event`, data => {
            if (Number(data.id) === Number(params.id)) {
                getRouting()
            }
        })

        channelDeleted.bind(`${clientId}-routing-deleted-event`, data => {
            if (Number(data.id) === Number(params.id)) {
                goBack()
            }
        })

        return (() => {
            pusher.unsubscribe(`${clientId}-routing-updated`)
            pusher.unsubscribe(`${clientId}-routing-deleted`)
        })
    }, [])

    useEffect(() => {
        getStatusOptions()
    }, [])

    useEffect(() => {
        getOperationNames()
    }, [])

    useEffect(() => {
        getProductionRateUom()
    }, [])

    useEffect(() => {
        if (siteId) {
            setSites([{ id: siteId, name: siteName }])
        }
    }, [siteId, siteName])

    useEffect(() => {
        if (isValid(siteId)) {
            getWorkCenters(siteId)
        }
    }, [siteId])

    const getRouting = async () => {
        setIsLoading(true)

        await axios.get(`/api/routings/${params.id}`, config).then(res => {
            const routing = res.data?.data

            setCode(routing?.routing_code ?? '')
            setName(routing?.routing_name ?? '')
            setSiteId(routing?.site_id ?? '')
            setSiteName(routing?.site_name ?? '')
            setDescription(routing?.description ?? '')
            setStatus(routing?.status ?? '')
            setOperations((routing?.routing_operations ?? []).map((op) => ({
                id: op.id,
                sequence: op.sequence,
                operation: op.operation_name,
                workCenterId: op.work_center_id,
                workCenterName: '',
                productionRate: op.production_rate,
                rateUom: op.production_rate_uom,
                setupTime: op.setup_time
            })))
        }).catch(({ response }) => {
            handleAxiosError({ response: response })
        }).finally(() => {
            setIsLoading(false)
        })
    }

    const getWorkCenters = async (site) => {
        await axios
            .get(`/api/work-centers?site_id=${site}`, config)
            .then(res => {
                const activeWorkCenters = (res.data?.data ?? []).filter(wc => String(wc.status).toLowerCase() === 'active')
                setWorkCenters(activeWorkCenters)
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getProductionRateUom = async () => {
        await axios
            .get('/api/production-rate-uom', config)
            .then(res => {
                setRateUomOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getStatusOptions = async () => {
        await axios
            .get('/api/routing-statuses', config)
            .then(res => {
                setStatusOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const getOperationNames = async () => {
        await axios
            .get('/api/routing-operation-names', config)
            .then(res => {
                setOperationOptions(res.data?.data ?? [])
            })
            .catch(({ response }) => {
                handleAxiosError({ response: response })
            })
    }

    const optionsSites = sites?.map(option => ({ label: option.name, id: option.id }))

    const operationsColumns = [
        { field: 'sequence', headerName: t('sequence'), flex: 0.5, minWidth: 90 },
        {
            field: 'operation',
            headerName: t('operation'),
            flex: 1,
            minWidth: 130,
            valueFormatter: ({ value }) => operationOptions.find(o => o.value === value)?.label ?? value
        },
        {
            field: 'workCenterId',
            headerName: t('work_center'),
            flex: 1,
            minWidth: 150,
            valueFormatter: ({ value }) => workCenters.find(wc => wc.id === value)?.work_center_name ?? ''
        },
        { field: 'productionRate', headerName: t('production_rate'), flex: 0.8, minWidth: 140 },
        {
            field: 'rateUom',
            headerName: t('rate_uom'),
            flex: 0.8,
            minWidth: 130,
            valueFormatter: ({ value }) => rateUomOptions.find(o => o.value === value)?.label ?? value
        },
        { field: 'setupTime', headerName: t('setup_time_hours'), flex: 0.8, minWidth: 150 }
    ]

    return (
        <>
            {isLoading ? <Loading /> : ''}
            <Box sx={{ background: '#F5F5FD', minHeight: '100vh' }}>
                <div className='flex justify-between items-center p-5 w-full bg-white' style={{ borderRadius: '5px 5px 0 0' }}>
                    <div className='flex items-center justify-between w-full leading-none'>
                        <p className='text-[18px] font-[600] text-[#333333] flex items-center'><span className='text-[#1ab800] text-[35px] mr-4'>•</span> {t('view_routing')} - {code}</p>
                        <div style={{ transform: "rotate(45deg)" }} className="font-[600] text-[#333333]">
                            <button onClick={goBack}><i className="fa-solid fa-plus"></i></button>
                        </div>
                    </div>
                </div>

                <div className='p-5 pb-12'>
                    <div className='bg-white p-6 pt-0 rounded-md w-full h-fit edit-view mb-3'>
                        <div className='pb-11 pt-1'>
                            <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('general_information')}</p>
                        </div>

                        <div className='grid grid-cols-1 lg:grid-cols-2 gap-x-5'>
                            {/* Code */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('code')}</p>
                                <TextField
                                    disabled
                                    value={code}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>

                            {/* Name */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('name')}</p>
                                <TextField
                                    disabled
                                    value={name}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>

                            {/* Site */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('site')}</p>
                                <Autocomplete
                                    disabled
                                    disablePortal
                                    disableClearable
                                    id="routing-site-autocomplete"
                                    options={optionsSites}
                                    value={siteId ? { label: siteName, id: siteId } : null}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    sx={{
                                        width: '100%', background: '#FCFCFC', '.MuiOutlinedInput-input': { padding: '0 !important' },
                                        boxShadow: 'none', '.MuiOutlinedInput-notchedOutline': { border: '1px solid #EEEFF2 !important' }
                                    }}
                                    renderInput={(params) => <TextField {...params} />}
                                />
                            </div>

                            {/* Status */}
                            <div className='w-full pb-4'>
                                <p className="text-[14px] text-[#718096]">{t('status')}</p>
                                <FormControl sx={{ width: '100%' }}>
                                    <Select
                                        disabled
                                        displayEmpty
                                        value={status}
                                        sx={TEXTFIELD_STYLE}
                                    >
                                        <MenuItem value="" disabled>{t('select_status')}</MenuItem>
                                        {statusOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>

                            {/* Description */}
                            <div className='w-full pb-4 lg:col-span-2'>
                                <p className="text-[14px] text-[#718096]">{t('description')}</p>
                                <TextField
                                    disabled
                                    multiline
                                    rows={3}
                                    value={description}
                                    sx={TEXTFIELD_STYLE}
                                />
                            </div>
                        </div>
                    </div>

                    <div className='bg-white pt-0 rounded-md w-full edit-view mb-3'>
                        <div className='pt-1 pl-3 p-5'>
                            <p className='text-[13px] font-[600] uppercase text-[#A1ACB8]'>{t('routing_operations')}</p>
                        </div>
                        <div className='border-b' style={{ opacity: 0.7, background: '#FCFCFC' }}>
                            <TableShared columns={operationsColumns} items={operations} disableColumnMenu={true} />
                        </div>
                    </div>

                    
                </div>
            </Box>
        </>
    )
}

export default ShowRouting
